type ShellSection = 'feed' | 'map' | 'offers' | 'profile';
export type UserContextDestination = 'visits' | 'saved' | 'friends';

type SessionPayload = {
  authenticated?: boolean;
  user?: {
    id?: string | null;
    slug?: string | null;
  } | null;
};

type VenueReferencePayload = {
  venue?: {
    id?: string | null;
  } | null;
};

const PATH_BY_SECTION: Record<ShellSection, string> = {
  feed: '/pulse',
  map: '/',
  offers: '/offers',
  profile: '/profile',
};

const SECTION_BY_PATH: Record<string, ShellSection> = {
  '/': 'map',
  '/pulse': 'feed',
  '/offers': 'offers',
  '/profile': 'profile',
  '/profile/photos': 'profile',
};

const NAV_INDEX_BY_SECTION: Record<ShellSection, number> = {
  feed: 0,
  map: 1,
  offers: 2,
  profile: 4,
};

const USER_CONTEXT_PATH_BY_DESTINATION: Record<UserContextDestination, string> = {
  visits: '/me/visits',
  saved: '/me/saved',
  friends: '/me/friends',
};

let installed = false;
let suppressNextHistoryPush = false;

function normalizedPathname(pathname: string = window.location.pathname): string {
  if (pathname === '/') return pathname;
  return pathname.replace(/\/+$/, '') || '/';
}

function notifyProfilePhotoView(full: boolean): void {
  window.dispatchEvent(new CustomEvent('hopper:profile-photo-view', { detail: { full } }));
}

function looksLikeUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function historyVenueId(pathname: string): string | null {
  if (normalizedPathname(pathname) !== normalizedPathname(window.location.pathname)) return null;

  const state = window.history.state as { hopperVenueId?: unknown } | null;
  return typeof state?.hopperVenueId === 'string' && state.hopperVenueId.trim() !== ''
    ? state.hopperVenueId.trim()
    : null;
}

async function resolveVenueReference(reference: string): Promise<string | null> {
  if (looksLikeUuid(reference)) return reference;

  try {
    const response = await fetch(`/api/v1/venues/${encodeURIComponent(reference)}`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;

    const payload = await response.json() as VenueReferencePayload;
    const venueId = payload.venue?.id?.trim();
    return venueId || null;
  } catch {
    return null;
  }
}

export function userContextPath(destination: UserContextDestination): string {
  return USER_CONTEXT_PATH_BY_DESTINATION[destination];
}

export function userContextDestinationForPath(pathname: string = window.location.pathname): UserContextDestination | null {
  const normalized = normalizedPathname(pathname);
  for (const [destination, path] of Object.entries(USER_CONTEXT_PATH_BY_DESTINATION) as Array<[UserContextDestination, string]>) {
    if (normalized === path) return destination;
  }
  return null;
}

export function venueIdForPath(pathname: string = window.location.pathname): string | null {
  const match = normalizedPathname(pathname).match(/^\/venue\/([^/]+)$/);
  if (!match) return null;

  const canonicalVenueId = historyVenueId(pathname);
  if (canonicalVenueId) return canonicalVenueId;

  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

export function isProfileSettingsPath(pathname: string = window.location.pathname): boolean {
  return normalizedPathname(pathname) === '/profile/settings';
}

export function isProfilePhotosPath(pathname: string = window.location.pathname): boolean {
  return normalizedPathname(pathname) === '/profile/photos';
}

export function profileReferenceForPath(pathname: string = window.location.pathname): string | null {
  const match = normalizedPathname(pathname).match(/^\/profile\/([^/]+)$/);
  if (!match || match[1] === 'settings' || match[1] === 'photos') return null;

  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

export function isOwnerProfileHistoryState(): boolean {
  const state = window.history.state as { hopperOwnerProfile?: unknown } | null;
  return state?.hopperOwnerProfile === true;
}

async function resolveOwnerProfileReference(): Promise<string | null> {
  try {
    const response = await fetch('/api/v1/auth/session', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;

    const session = await response.json() as SessionPayload;
    if (!session.authenticated || !session.user?.id) return null;

    const slug = session.user.slug?.trim();
    return slug || session.user.id;
  } catch {
    return null;
  }
}

function ownerProfileState(profileReference: string): Record<string, unknown> {
  return {
    hopperSection: 'profile',
    hopperOwnerProfile: true,
    hopperProfileReference: profileReference,
  };
}

export async function pushCanonicalOwnerProfilePath(): Promise<void> {
  const reference = await resolveOwnerProfileReference();
  if (!reference) return;
  pushOwnerProfilePath(reference);
}

export async function replaceCanonicalOwnerProfilePath(): Promise<void> {
  const reference = await resolveOwnerProfileReference();
  if (!reference) return;
  replaceOwnerProfilePath(reference);
}

function sectionFromLocation(): ShellSection | null {
  if (isProfilePhotosPath()) return 'profile';
  if (profileReferenceForPath() && isOwnerProfileHistoryState()) {
    return 'profile';
  }
  return SECTION_BY_PATH[normalizedPathname()] ?? null;
}

function semanticSectionFromButton(button: HTMLButtonElement): ShellSection | null {
  const section = button.dataset.shellSection;
  if (section === 'feed' || section === 'map' || section === 'offers' || section === 'profile') {
    return section;
  }

  return null;
}

function sectionFromButton(button: HTMLButtonElement): ShellSection | null {
  if (button.classList.contains('user-avatar')) return 'profile';
  const semanticSection = semanticSectionFromButton(button);
  if (semanticSection) return semanticSection;

  const navSelectors = [
    '.pulse-mobile-nav__item',
  ];

  for (const selector of navSelectors) {
    if (!button.matches(selector)) continue;

    const buttons = Array.from(document.querySelectorAll<HTMLButtonElement>(selector));
    const index = buttons.indexOf(button);

    if (index === 0) return 'feed';
    if (index === 1) return 'map';
    if (index === 2) return 'offers';
    if (index === 4) return 'profile';
    return null;
  }

  return null;
}

function navigationButton(section: ShellSection): HTMLButtonElement | null {
  const semanticButton = document.querySelector<HTMLButtonElement>(
    `.desktop-nav__item[data-shell-section="${section}"], .mobile-nav-item[data-shell-section="${section}"]`,
  );
  if (semanticButton) return semanticButton;

  const index = NAV_INDEX_BY_SECTION[section];
  const selectors = [
    '.pulse-mobile-nav__item',
  ];

  for (const selector of selectors) {
    const buttons = document.querySelectorAll<HTMLButtonElement>(selector);
    const button = buttons.item(index);
    if (button) return button;
  }

  return null;
}

function dispatchVenueHistory(venueId: string): void {
  window.dispatchEvent(new CustomEvent('hopper:history-venue', { detail: { venueId } }));
}

function applyLocationToShell(attempt = 0): void {
  const userContextDestination = userContextDestinationForPath();
  if (userContextDestination) {
    suppressNextHistoryPush = false;
    window.dispatchEvent(new CustomEvent('hopper:history-user-context', { detail: { destination: userContextDestination } }));
    return;
  }

  const venueReference = venueIdForPath();
  if (venueReference) {
    suppressNextHistoryPush = false;

    if (looksLikeUuid(venueReference)) {
      dispatchVenueHistory(venueReference);
      return;
    }

    const expectedPath = normalizedPathname();
    void resolveVenueReference(venueReference).then((venueId) => {
      if (!venueId || normalizedPathname() !== expectedPath) return;

      const url = new URL(window.location.href);
      window.history.replaceState(
        { ...(window.history.state ?? {}), hopperVenueId: venueId },
        '',
        `${url.pathname}${url.search}${url.hash}`,
      );
      dispatchVenueHistory(venueId);
    });
    return;
  }

  if (isProfileSettingsPath()) {
    suppressNextHistoryPush = false;
    window.dispatchEvent(new Event('hopper:history-profile-settings'));
    return;
  }

  const profilePhotos = isProfilePhotosPath();
  if (profilePhotos) {
    notifyProfilePhotoView(true);
  }

  const profileReference = profileReferenceForPath();
  if (profileReference && !isOwnerProfileHistoryState()) {
    suppressNextHistoryPush = false;
    notifyProfilePhotoView(false);
    window.dispatchEvent(new CustomEvent('hopper:history-user-profile', {
      detail: { profileReference },
    }));
    return;
  }

  const section = sectionFromLocation();
  if (!section) {
    suppressNextHistoryPush = false;
    return;
  }

  if (section === 'profile' && normalizedPathname() === '/profile') {
    void replaceCanonicalOwnerProfilePath();
  } else if (section === 'profile' && !profilePhotos) {
    notifyProfilePhotoView(false);
  }

  window.dispatchEvent(new CustomEvent('hopper:history-section', { detail: { section } }));

  const button = navigationButton(section);
  if (!button) {
    if (attempt < 20) {
      window.requestAnimationFrame(() => applyLocationToShell(attempt + 1));
      return;
    }
    suppressNextHistoryPush = false;
    return;
  }

  suppressNextHistoryPush = true;
  button.click();
}

/**
 * Re-applies the current URL/history state to the mounted legacy shell.
 * Profile-family routing uses this after resolving a UUID to the current owner
 * so the existing ProfileScreen becomes active without reloading the page.
 */
export function syncShellHistoryFromLocation(): void {
  window.requestAnimationFrame(() => applyLocationToShell());
}

function pushPath(path: string, state: Record<string, unknown>): void {
  if (normalizedPathname() === path && isOwnerProfileHistoryState() === (state.hopperOwnerProfile === true)) return;
  window.history.pushState(state, '', path);
}

export function pushVenuePath(venueId: string): void {
  pushPath(`/venue/${encodeURIComponent(venueId)}`, { hopperVenueId: venueId });
}

export function pushProfileSettingsPath(): void {
  pushPath('/profile/settings', { hopperProfileSettings: true });
}

export function pushProfilePhotosPath(): void {
  pushPath('/profile/photos', {
    hopperSection: 'profile',
    hopperOwnerProfile: true,
    hopperProfilePhotos: true,
  });
  notifyProfilePhotoView(true);
}

export function pushPublicProfilePath(profileReference: string): void {
  pushPath(`/profile/${encodeURIComponent(profileReference)}`, { hopperProfileReference: profileReference });
}

export function pushOwnerProfilePath(profileReference: string): void {
  pushPath(`/profile/${encodeURIComponent(profileReference)}`, ownerProfileState(profileReference));
  notifyProfilePhotoView(false);
}

export function replaceOwnerProfilePath(profileReference: string): void {
  const url = new URL(window.location.href);
  const suffix = `${url.search}${url.hash}`;

  window.history.replaceState(
    ownerProfileState(profileReference),
    '',
    `/profile/${encodeURIComponent(profileReference)}${suffix}`,
  );
  notifyProfilePhotoView(false);
}

export function pushShellSectionPath(section: ShellSection): void {
  pushPath(PATH_BY_SECTION[section], { hopperSection: section });
  if (section === 'profile') notifyProfilePhotoView(false);
}

export function replaceWithMapPath(): void {
  if (normalizedPathname() === '/') return;
  window.history.replaceState({ hopperSection: 'map' }, '', '/');
}

export function installShellHistory(): void {
  if (installed) return;
  installed = true;

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest<HTMLButtonElement>('button');
    if (!button) return;

    const section = sectionFromButton(button);
    if (!section) return;

    if (suppressNextHistoryPush) {
      suppressNextHistoryPush = false;
      return;
    }

    if (section === 'profile') {
      void pushCanonicalOwnerProfilePath();
      return;
    }

    const nextPath = PATH_BY_SECTION[section];
    if (normalizedPathname() === nextPath) return;
    window.history.pushState({ hopperSection: section }, '', nextPath);
  }, true);

  window.addEventListener('popstate', () => applyLocationToShell());
  window.requestAnimationFrame(() => applyLocationToShell());
}
